import React, { useMemo } from 'react';
// import type { Task } from 'src/types/task.type';
import type { Task } from '../types/task.type';

export default function TaskSummary({ tasks, userNameMap }: { tasks: Task[]; userNameMap?: Record<string, string> }) {
    const means = useMemo(() => {
        const valid = tasks.filter((t) => typeof t.urgency === 'number' && typeof t.importance === 'number');
        if (!valid.length) return { avgScore: 0, avgU: 0, avgI: 0 };
        const sumU = valid.reduce((s, t) => s + t.urgency, 0);
        const sumI = valid.reduce((s, t) => s + t.importance, 0);
        const avgU = sumU / valid.length;
        const avgI = sumI / valid.length;
        return { avgScore: avgU * avgI, avgU, avgI };
    }, [tasks]);

    const statusStats = useMemo(() => {
        const total = tasks.length || 1;
        const done = tasks.filter((t) => t.status === 'DONE' || !!t.closedAt).length;
        const open = total - done;
        const donePct = Math.round((done / total) * 100);

        const today = new Date();
        today.setHours(0, 0, 0, 0);
        let overdue = 0;
        for (const t of tasks) {
            if (t.status === 'DONE' || t.closedAt) continue;
            if (t.dueDate) {
                const due = Date.parse(t.dueDate);
                if (due && due < today.getTime()) overdue++;
            }
        }
        return { open, done, donePct, overdue };
    }, [tasks]);

    const topAssignees = useMemo(() => {
        const map = new Map<string, number>();
        tasks.forEach((t) => {
            const k = String(t.assigneeUserId ?? '—');
            map.set(k, (map.get(k) || 0) + 1);
        });
        return [...map.entries()].sort((a, b) => b[1] - a[1]).slice(0, 5);
    }, [tasks]);

    const labelOf = (uid: string) => userNameMap?.[uid] ?? uid;

    return (
        <div className="grid grid-cols-3 gap-3 auto-rows-fr">
            <div className="rounded-lg border p-3">
                <div className="text-xs text-gray-500 mb-1">Averages</div>
                <div className="text-sm">
                    Score <b>{means.avgScore ? means.avgScore.toFixed(1) : '0.0'}</b>
                </div>
                <div className="text-xs text-gray-700 mt-1">
                    U {means.avgU.toFixed(1)} · I {means.avgI.toFixed(1)}
                </div>
            </div>

            <div className="rounded-lg border p-3">
                <div className="text-xs text-gray-500 mb-1">Status</div>
                <div className="text-sm">
                    Open <b>{statusStats.open}</b>
                </div>
                <div className="text-sm">
                    Done <b>{statusStats.done}</b>
                </div>
                <div className="text-xs text-gray-500">Done % {statusStats.donePct}%</div>
                <div className="text-xs text-rose-600 mt-1">Overdue {statusStats.overdue}</div>
            </div>

            <div className="rounded-lg border p-3">
                <div className="text-xs text-gray-500 mb-1">Top Assignees</div>
                <ul className="text-sm space-y-1">
                    {topAssignees.length ? (
                        topAssignees.map(([uid, cnt]) => (
                            <li key={uid} className="flex justify-between">
                                <span className="truncate max-w-[220px]">{uid === '—' ? '—' : labelOf(uid)}</span>
                                <b>{cnt}</b>
                            </li>
                        ))
                    ) : (
                        <li className="text-xs text-gray-400">No data</li>
                    )}
                </ul>
            </div>
        </div>
    );
}
